import React from 'react';
import type {IconName, IconPrefix} from '@fortawesome/fontawesome-common-types';

import {Icon, Text} from '@albert-io/atomic';

interface InviteMethod {
  modalKey: string;
  title: string;
  description: string;
  icon: IconName | [IconPrefix, IconName];
  iconStyle: 'light' | 'regular';
  iconClassName: string;
  testId: string;
}

const METHODS: InviteMethod[] = [
  {
    modalKey: 'LMS',
    title: 'Import from LMS',
    description:
      'Import students from Canvas, Schoology or Brightspace and unlock assignment and grade syncing.',
    icon: 'school',
    iconStyle: 'light',
    iconClassName: 'background-grape-subtle content-grape',
    testId: 'invite-card__from-lms'
  },
  {
    modalKey: 'googleClassroom',
    title: 'Import from Google Classroom',
    description: 'Import your rosters from Google and unlock assignment posting.',
    icon: ['fab', 'google'],
    iconStyle: 'regular',
    iconClassName: 'background-kiwi-subtle content-kiwi',
    testId: 'invite-card__from-google'
  },
  {
    modalKey: 'classCode',
    title: 'Display Class Code',
    description: 'Give students a code they can use to join this class.',
    icon: 'barcode',
    iconStyle: 'light',
    iconClassName: 'background-clementine-subtle content-clementine',
    testId: 'invite-card__by-code'
  },
  {
    modalKey: 'email',
    title: 'Invite by Email',
    description: 'Send invites to one or multiple student emails.',
    icon: 'envelope',
    iconStyle: 'light',
    iconClassName: 'background-dragonfruit-subtle content-dragonfruit',
    testId: 'invite-card__by-email'
  }
];

interface Props {
  onShowModal: (modal: string) => void;
}

export const InviteMethodCards = ({onShowModal}: Props) => (
  <ul className='m-x0 gap-x2 p-x0 m:grid-cols-2 grid list-none grid-cols-1'>
    {METHODS.map((method) => (
      <li key={method.modalKey}>
        {/* Raw button: card-layout tile; focus ring mirrors atomic $outline-focus width with the
            semantic brand color. */}
        <button
          className='gap-x1-half rounded-x1-half border-border-primary background-primary p-x3 hover:background-primary-hover active:background-primary-pressed flex h-full w-full cursor-pointer items-center border text-left focus-visible:[outline:3px_solid_var(--color-content-brand)]'
          data-testid={method.testId}
          onClick={() => onShowModal(method.modalKey)}
          type='button'
        >
          <div className='flex h-full grow flex-col items-start justify-between'>
            <span
              className={`h-x5 w-x5 rounded-x1 flex items-center justify-center text-xl ${method.iconClassName}`}
            >
              <Icon
                color='inherit'
                icon={method.icon}
                iconStyle={method.iconStyle}
                size='inherit'
              />
            </span>
            <div className='gap-x0-quarter pt-x2 flex flex-col'>
              <Text color='primary' size='regular'>{method.title}</Text>
              <Text color='tertiary' size='s'>
                {method.description}
              </Text>
            </div>
          </div>
          <Icon className='content-tertiary shrink-0' icon='chevron-right' iconStyle='regular' />
        </button>
      </li>
    ))}
  </ul>
);
